import { twAuthClient } from "@teamworksdev/web-sdk/auth";
import { useTWAuthClient } from "@teamworksdev/web-sdk/auth-react";
import { createContext, ReactNode, useContext, useRef, useState } from "react";
import { useFetchUnityTokenMutation } from "../types";
import { useUser } from "../utils/tokens";

interface TeamworksAuthContext {
  handleTwAuthClientState: () => Promise<void>;
  shouldAuthTwClient: boolean;
  isAuthReady: boolean;
}

const TeamworksAuthContext = createContext<TeamworksAuthContext>({
  handleTwAuthClientState: async () => {},
  shouldAuthTwClient: false,
  isAuthReady: false,
});

interface TeamworksAuthContextProviderProps {
  children: ReactNode;
}

export const TeamworksAuthContextProvider = ({ children }: TeamworksAuthContextProviderProps) => {
  const { initialized, token } = useTWAuthClient();
  const user = useUser();
  const [authInitComplete, setAuthInitComplete] = useState(false);
  const initInFlight = useRef(false);

  const axlePersonDocumentId = user?.orgMembership?.axlePersonDocumentId;
  const shouldFetchToken = !!axlePersonDocumentId;

  const [fetchUnityToken] = useFetchUnityTokenMutation();

  const fetchTokenCallback = async () => {
    const result = await fetchUnityToken();
    const token = result.data?.fetchUnityToken?.token;
    return token || null;
  };

  const handleTwAuthClientState = async () => {
    if (shouldFetchToken === false) {
      if (initialized) {
        // User switched from an axle-synced org to an org that is not
        await twAuthClient.reset({ defaultRefreshTokenEnabled: false });
        // ^ Note: init() and reset() share the same options, so we won't need to pass the callback fn again
        // our callback fn will return null once and then stop fetching
        initInFlight.current = false;
        setAuthInitComplete(false);
      }
      return;
    } else {
      if (initialized) {
        // This reset will fetch a token valid to the current org membership and make sure refreshes are enabled
        await twAuthClient.reset({ defaultRefreshTokenEnabled: true });
        setAuthInitComplete(true);
      } else if (!initInFlight.current) {
        initInFlight.current = true;
        await twAuthClient.init({ fetchToken: fetchTokenCallback });
        setAuthInitComplete(true);
      }
    }
  };
  const isAuthReady = shouldFetchToken && authInitComplete && !!token;
  return <TeamworksAuthContext.Provider value={{ handleTwAuthClientState, shouldAuthTwClient: shouldFetchToken, isAuthReady }}>{children}</TeamworksAuthContext.Provider>;
};

export const useTeamworksAuthContext = () => useContext(TeamworksAuthContext);
